import React, { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaPaperPlane, FaCheck } from 'react-icons/fa';
import Reveal from '../Reveal';
import {
  ContactSection,
  Container,
  ContactInfo,
  SectionTag,
  SectionTitle,
  SectionSubtitle,
  ContactHighlights,
  HighlightCard,
  ContactList,
  ContactItem,
  ContactIcon,
  ContactText,
  FormCard,
  FormIntro,
  FormRow,
  FormGroup,
  Input,
  Textarea,
  SubmitBtn,
  SuccessMsg,
} from './Contact.styles';

interface FormState {
  name: string;
  email: string;
  subject: string;
  message: string;
}

const Contact: React.FC = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const contactDetails = [
    { href: 'mailto:bart@ercomp.co.uk', icon: <FaEnvelope />, label: t('contact.emailLabel2'), value: 'bart@ercomp.co.uk' },
    { href: 'tel:+447964800555', icon: <FaPhone />, label: t('contact.phoneLabel'), value: '+44 7964 800555' },
    { href: '#', icon: <FaMapMarkerAlt />, label: t('contact.locationLabel'), value: t('contact.locationValue') },
  ];

  const highlights = [
    { label: t('contact.card0Label'), value: t('contact.card0Value') },
    { label: t('contact.card1Label'), value: t('contact.card1Value') },
    { label: t('contact.card2Label'), value: t('contact.card2Value') },
  ];

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSent(true);
      setForm({ name: '', email: '', subject: '', message: '' });
    }, 1500);
  };

  return (
    <ContactSection id="kontakt">
      <Container>
        <Reveal>
          <ContactInfo>
            <SectionTag>{t('contact.tag')}</SectionTag>
            <SectionTitle>
              {t('contact.title')} <span>{t('contact.titleGradient')}</span>
            </SectionTitle>
            <SectionSubtitle>{t('contact.subtitle')}</SectionSubtitle>

            <ContactHighlights>
              {highlights.map((item) => (
                <HighlightCard key={item.label}>
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </HighlightCard>
              ))}
            </ContactHighlights>

            <ContactList>
              {contactDetails.map((c) => (
                <ContactItem key={c.label} href={c.href}>
                  <ContactIcon>{c.icon}</ContactIcon>
                  <ContactText>
                    <div className="label">{c.label}</div>
                    <div className="value">{c.value}</div>
                  </ContactText>
                </ContactItem>
              ))}
            </ContactList>
          </ContactInfo>
        </Reveal>

        <Reveal delay={110}>
          <FormCard onSubmit={onSubmit}>
            <FormIntro>
              <span>{t('contact.briefLabel')}</span>
              <h3>{t('contact.briefTitle')}</h3>
              <p>{t('contact.briefSubtitle')}</p>
            </FormIntro>

            <FormRow>
              <FormGroup>
                <label htmlFor="name">{t('contact.nameLabel')}</label>
                <Input
                  id="name"
                  name="name"
                  placeholder={t('contact.namePlaceholder')}
                  value={form.name}
                  onChange={onChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <label htmlFor="email">{t('contact.emailLabel')}</label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder={t('contact.emailPlaceholder')}
                  value={form.email}
                  onChange={onChange}
                  required
                />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <label htmlFor="subject">{t('contact.subjectLabel')}</label>
              <Input
                id="subject"
                name="subject"
                placeholder={t('contact.subjectPlaceholder')}
                value={form.subject}
                onChange={onChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <label htmlFor="message">{t('contact.messageLabel')}</label>
              <Textarea
                id="message"
                name="message"
                placeholder={t('contact.messagePlaceholder')}
                value={form.message}
                onChange={onChange}
                required
              />
            </FormGroup>

            <SubmitBtn type="submit" $loading={loading}>
              {loading ? t('contact.sending') : <>{t('contact.send')} <FaPaperPlane size={13} /></>}
            </SubmitBtn>

            {sent && (
              <SuccessMsg>
                <FaCheck /> {t('contact.success')}
              </SuccessMsg>
            )}
          </FormCard>
        </Reveal>
      </Container>
    </ContactSection>
  );
};

export default Contact;
